import type { DownloadAuth, ModelDetail, RelatedModel } from '@/types/model'
import { httpClient } from '@/utils/request'

const BASE_URL = '/models'

// 对应后端 ModelDetailPublic Schema（snake_case，与 list.ts 的 BackendModelPublic 保持一致策略）
export interface BackendModelDetailResponse {
  id: string | number
  serial_number?: string
  title?: string
  name?: string
  description?: string
  images?: string[]
  cover_image?: string
  software?: string
  software_version?: string
  format?: string
  formats?: string[]
  file_size?: number // 后端类型为 int（字节数）
  category: string
  category_l1_code?: string
  category_l2_code?: string
  editable?: 'non-editable' | 'editable-no-params' | 'editable-with-params'
  price?: number
  vip_price?: number
  is_free?: boolean
  view_count?: number
  download_count?: number
  favorite_count?: number
  published_at?: string
  created_at?: string
  updated_at?: string

  // author 嵌套对象保持 camelCase（后端 UserPublic 配置了 alias_generator=to_camel）
  author?: {
    id: string | number
    fullName?: string
    nickname?: string
    avatarFile?: string
    avatar?: string // computed_field — 后端返回完整头像 URL
  }
  is_purchased?: boolean
  is_liked?: boolean
  can_download?: boolean
  files?: {
    id: string
    parent_id?: string | null
    file_name: string
    file_type: string
    file_size: number
    file_format: string | null
    file_url: string | null
    sort_order: number
  }[]
}

// 对应后端 ModelPublic Schema（snake_case）
export interface BackendRelatedModelResponse {
  id: string | number
  title?: string
  name?: string
  cover_image?: string
  software?: string
  format?: string
  price?: number
  download_count?: number
  favorite_count?: number
  is_liked?: boolean
}

export interface DownloadUrlResponse {
  downloadUrl?: string
  download_url?: string
  fileName?: string
  file_name?: string
}

export async function getModelDetail(id: string | number): Promise<ModelDetail> {
  const res = await httpClient.get<BackendModelDetailResponse>(`${BASE_URL}/${id}`)

  // 文件大小格式化
  function formatBytes(bytes: number): string {
    if (bytes === 0)
      return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${(bytes / 1024 ** i).toFixed(1)} ${units[i]}`
  }

  // 适配组装 BackendModelDetailResponse -> ModelDetail（snake_case 映射）
  const modelContent: ModelDetail = {
    id: res.id,
    name: res.title || res.name || '未命名模型',
    description: res.description || '',
    serialNumber: res.serial_number || '',
    images: res.images || (res.cover_image ? [res.cover_image] : []),
    software: res.software || '',
    softwareVersion: res.software_version || '',
    format: (res.formats && res.formats.length > 0) ? res.formats.join(', ') : (res.format || ''),
    fileSize: (() => {
      let size = res.file_size || 0
      if (!size && res.files && res.files.length > 0) {
        const rootFile = res.files.find(f => f.file_url)
        if (rootFile)
          size = rootFile.file_size
      }
      return size > 0 ? formatBytes(size) : '未知大小'
    })(),
    uploadDate: res.published_at || res.created_at || '',
    category: res.category_l1_code || res.category || '',
    editable: res.editable || 'non-editable',
    pricing: {
      price: typeof res.price === 'number' ? res.price : 0,
      vipPrice: typeof res.vip_price === 'number' ? res.vip_price : undefined,
      isFree: res.is_free ?? false,
      currency: 'points',
    },
    purchaseStatus: res.is_purchased ? 'purchased' : (res.is_free ? 'free' : 'notPurchased'),
    downloadAuth: {
      canDownload: res.can_download ?? false,
    },
    isOwner: false, // 后端 ModelDetailPublic 不含 is_owner 字段
    isFavorited: res.is_liked ?? false,
    favoriteCount: res.favorite_count ?? 0,
    stats: {
      viewCount: res.view_count ?? 0,
      downloadCount: res.download_count ?? 0,
    },
    files: (res.files || []).map(f => ({
      id: f.id,
      parentId: f.parent_id || undefined,
      name: f.file_name,
      type: (f.file_type === 'folder' ? 'folder' : 'file') as 'folder' | 'file',
      size: formatBytes(f.file_size),
    })),
    author: {
      id: res.author?.id || 0,
      name: res.author?.nickname || res.author?.fullName || '未知作者',
      avatar: res.author?.avatar || res.author?.avatarFile || '', // 优先使用 avatar（完整 URL）
      description: '',
      stats: {
        models: 0,
        downloads: 0,
        orders: 0,
        rating: 0,
      },
    },
  }
  return modelContent
}

export async function getRelatedModels(id: string | number): Promise<RelatedModel[]> {
  const rawList = await httpClient.get<BackendRelatedModelResponse[]>(`${BASE_URL}/${id}/related`)
  return rawList.map(item => ({
    id: item.id,
    name: item.title || item.name || '',
    imageUrl: item.cover_image || '',
    software: item.software || '',
    format: item.format || '',
    price: item.price || 0,
    isPurchased: false,
    isLiked: item.is_liked ?? false,
    downloads: item.download_count || 0,
    likes: item.favorite_count || 0,
  }))
}

export async function getModelDownloadUrl(id: string | number): Promise<DownloadAuth> {
  const res = await httpClient.post<DownloadUrlResponse>(`${BASE_URL}/${id}/download`)
  return {
    canDownload: true,
    downloadUrl: res.downloadUrl || res.download_url,
    fileName: res.fileName || res.file_name,
  }
}
